/**
 * GL9300C Budget Upload Transformer
 *
 * Converts a flat budget spreadsheet into the GL9300C keyword-based format.
 *
 * Source expectations:
 *   Columns: unit, account, [project_type, project_id], jan, feb, ..., dec
 *   One row per account per unit.
 *
 * Output format (GL9300C):
 *   Row 1: Budget ID | <value>
 *   Row 2: Description | <value>
 *   Row 3: Company ID | <value>
 *   Row 4: Type | R or I
 *   Row 5: Periods | 12
 *   Row 6: (blank)
 *   Row 7: Unit | Account | ProjType | ProjId | DataType | Remarks | (blank) | (blank) | JAN-YY | FEB-YY | ... | DEC-YY
 *   Row 8+: <unit> | <acct> | ... | <amounts>
 *
 * Rules applied:
 *   - Revenue accounts (5xxxxx): amounts stay positive (MV auto-negates)
 *   - Amount scaling: multiply by 100 for implied-decimal format
 *   - Excluded accounts filtered out
 */
import * as XLSX from 'xlsx';
import { EXCLUDED_ACCOUNTS as EXCLUDED } from '../lib/excluded-accounts';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

// Detect revenue accounts: 5xxxxx series
const isRevenue = (acct) => /^5\d{5}$/.test(String(acct));

export function transformBudget(data, sourceWb) {
  // Try to detect metadata from the source or use defaults
  const meta = detectMeta(data, sourceWb);

  // Filter out excluded accounts and blank rows
  const filtered = data.filter((row) => {
    const acct = String(row.account ?? row.account_no ?? '').trim();
    return acct && !EXCLUDED.has(acct);
  });

  // Build the output workbook
  const wb = XLSX.utils.book_new();
  const sheetData = [];

  // Fiscal year for period headers (default to current + 1)
  const fy = meta.fiscalYear || new Date().getFullYear() + 1;
  const fyShort = String(fy).slice(-2);

  // Group by company if we have company info, otherwise single block
  const companies = groupByCompany(filtered, meta);

  for (const [companyId, rows] of Object.entries(companies)) {
    // Header block
    sheetData.push(['Budget ID', meta.budgetId || `${fy}BU`]);
    sheetData.push(['Description', meta.description || `FY${fy} Budget`]);
    sheetData.push(['Company ID', companyId]);
    sheetData.push(['Type', meta.type || 'R']);
    sheetData.push(['Periods', 12]);
    sheetData.push([]); // blank row

    // Unit/header row with period dates
    const headerRow = [
      'Unit', 'Account', 'Project Type', 'Project', 'Data Type', 'Remarks', '', '',
      ...MONTHS.map((m) => `${m.toUpperCase()}-${fyShort}`),
    ];
    sheetData.push(headerRow);

    // Data rows
    rows.forEach((row) => {
      const acct = String(row.account ?? row.account_no ?? '').trim();
      const unit = String(row.unit ?? row.unit_id ?? '').trim().padStart(3, '0');
      const projType = String(row.project_type ?? '').trim();
      const projId = String(row.project_id ?? row.project ?? '').trim();
      const dataType = String(row.data_type ?? '').trim();
      const remarks = String(row.remarks ?? '').trim();

      const amounts = MONTHS.map((m) => {
        let val = Number(row[m] ?? 0) || 0;
        // Revenue sign-flipping: keep positive — MV auto-negates.
        // If the source has them negative, flip to positive.
        if (isRevenue(acct) && val < 0) {
          val = Math.abs(val);
        }
        // Amount scaling: multiply by 100 for implied 2-decimal format
        return Math.round(val * 100);
      });

      sheetData.push([unit, acct, projType, projId, dataType, remarks, '', '', ...amounts]);
    });

    sheetData.push([]); // blank row between companies
  }

  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  XLSX.utils.book_append_sheet(wb, ws, 'Budget');

  // Build preview rows for the validation view
  const previewRows = filtered.map((row) => {
    const acct = String(row.account ?? row.account_no ?? '').trim();
    const unit = String(row.unit ?? row.unit_id ?? '').trim().padStart(3, '0');
    const result = { unit, account: acct };
    MONTHS.forEach((m) => {
      let val = Number(row[m] ?? 0) || 0;
      if (isRevenue(acct) && val < 0) val = Math.abs(val);
      result[m.toUpperCase()] = Math.round(val * 100);
    });
    return result;
  });

  return { workbook: wb, rows: previewRows, meta };
}

function detectMeta(data, sourceWb) {
  // Attempt to read metadata from source workbook if it has keyword rows
  const meta = {
    budgetId: '',
    description: '',
    companyId: '',
    type: 'R',
    fiscalYear: new Date().getFullYear() + 1,
  };

  // Check if source has keyword-style rows (Budget ID, Company ID, etc.)
  const firstSheet = sourceWb.Sheets[sourceWb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });

  for (const row of raw.slice(0, 20)) {
    const keyword = String(row[0] ?? '').trim().toLowerCase();
    const value = row[1];
    if (keyword === 'budget id' && value) meta.budgetId = String(value);
    if (keyword === 'company id' && value) meta.companyId = String(value);
    if (keyword === 'description' && value) meta.description = String(value);
    if (keyword === 'type' && value) meta.type = String(value).toUpperCase();
  }

  return meta;
}

function groupByCompany(rows, meta) {
  // If rows have a company field, group by it; otherwise use meta.companyId or default
  const groups = {};
  rows.forEach((row) => {
    const cid = String((row.company_id ?? row.company ?? meta.companyId) || '01').trim();
    if (!groups[cid]) groups[cid] = [];
    groups[cid].push(row);
  });
  return groups;
}
